/**
 * Página de acompanhamento de comissões
 *
 * Responsável por:
 * - Buscar dados da API
 * - Gerenciar estado de loading
 * - Gerenciar filtros de data
 * - Renderizar componentes de UI
 *
 * @module pages/CommissionAccompaniment
 */
import { getAccompanimentCommissionData } from "@/Api/commissionConfig";
import { TableData } from "@/App/CommissionAccompaniment/Table";
import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { SelectComponent } from "@/components/SelectComponent";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CommissionAccompanimentData } from "@/types/commissionAccompaniment";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon, Search, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { getComissionSelectConfig } from "@/Api/commissionConfig";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { PageHeader } from "@/components/PageHeader";

/**
 * Componente da página de acompanhamento de comissões
 */
export const PageList = () => {
  const [state, setState] = useState("loading");
  const [commissionsOptions, setCommissionsOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedCommission, setSelectedCommission] = useState<string | null>(
    null
  );
  const [dateRange, setDateRange] = useState<{
    startDate: Date | undefined;
    endDate: Date | undefined;
  }>({
    startDate: undefined,
    endDate: undefined,
  });
  const [accompanimentData, setAccompanimentData] =
    useState<CommissionAccompanimentData | null>(null);
  const { hasPermission } = usePermissions();

  // Carrega as opções de comissões para o select
  useEffect(() => {
    (async () => {
      try {
        const { data } = await getComissionSelectConfig();

        if (data?.response) {
          const options = data.response.map((item: any) => ({
            label: item.name,
            value: item.uuid,
          }));

          setCommissionsOptions(options);

          // Se há opções, seleciona a primeira por padrão
          if (options.length > 0) {
            setSelectedCommission(options[0].value);
          }
        }

        setState("production");
      } catch (error) {
        console.error("Erro ao carregar comissões:", error);
        setState("error");
      }
    })();
  }, []);

  // Carrega os dados de acompanhamento quando uma comissão é selecionada
  useEffect(() => {
    if (selectedCommission) {
      loadAccompanimentData();
    }
  }, [selectedCommission]);

  // Função para carregar os dados de acompanhamento
  const loadAccompanimentData = async () => {
    if (!selectedCommission) return;

    setState("loading");

    try {
      const startDateParam = dateRange.startDate
        ? format(dateRange.startDate, "yyyy-MM-dd")
        : undefined;

      const endDateParam = dateRange.endDate
        ? format(dateRange.endDate, "yyyy-MM-dd")
        : undefined;

      const { data } = await getAccompanimentCommissionData(
        selectedCommission,
        startDateParam,
        endDateParam
      );

      if (data?.response) {
        setAccompanimentData(data.response);
      }

      setState("production");
    } catch (error) {
      console.error("Erro ao carregar dados de acompanhamento:", error);
      setState("error");
    }
  };

  // Manipula a busca por período
  const handleSearch = () => {
    loadAccompanimentData();
  };

  return (
    <Main>
      <If condition={hasPermission("view-commission-tracking-list")}>
        <PageHeader
          icon={TrendingUp}
          title="Acompanhamento de Comissoes"
          description="Acompanhe as comissoes por periodo"
        />

        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {/* Select de comissões */}
            <div className="md:col-span-2">
              <SelectComponent
                label="Comissão"
                options={commissionsOptions}
                onChange={(value) => setSelectedCommission(value as string)}
                value={selectedCommission || ""}
                placeholder="Selecione uma comissão"
              />
            </div>

            {/* Período inicial */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Data Inicial
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dateRange.startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.startDate ? (
                      format(dateRange.startDate, "dd/MM/yyyy", {
                        locale: ptBR,
                      })
                    ) : (
                      <span>Selecione uma data</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={dateRange.startDate}
                    onSelect={(date) =>
                      setDateRange((prev) => ({ ...prev, startDate: date }))
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Período final */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Data Final
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !dateRange.endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange.endDate ? (
                      format(dateRange.endDate, "dd/MM/yyyy", { locale: ptBR })
                    ) : (
                      <span>Selecione uma data</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={dateRange.endDate}
                    onSelect={(date) =>
                      setDateRange((prev) => ({ ...prev, endDate: date }))
                    }
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Botão de busca */}
            <div className="md:col-span-4 flex justify-end">
              <Button
                className="bg-indigo-500 hover:bg-indigo-600"
                onClick={handleSearch}
              >
                <Search className="mr-2 h-4 w-4" /> Buscar
              </Button>
            </div>
          </div>

          {/* Tabela de resultados */}
          {state === "loading" ? (
            <CreateLoaderTable cols={6} lines={6} />
          ) : (
            accompanimentData && <TableData data={accompanimentData} />
          )}
        </div>
      </If>
    </Main>
  );
};
